import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { streamSse } from "@/lib/sse";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Trash2, X } from "lucide-react";

const SUGGESTIONS = ["What is blocking this project right now?", "Summarise the status of every phase.", "What is still missing for ERP readiness?", "Who owns the overdue actions?"];

export const ProjectAssistant = ({ project, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [model, setModel] = useState("");
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  const endRef = useRef(null);

  const load = () => api.get(`/projects/${project.project_id}/assistant/messages`).then((r) => { setMessages(r.data.messages); setModel(r.data.model); });
  useEffect(() => { load(); }, [project.project_id]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, draft]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput(""); setStreaming(true); setDraft("");
    setMessages((m) => [...m, { message_id: `tmp_${Date.now()}`, role: "user", text: q }]);
    await streamSse(`/projects/${project.project_id}/assistant/messages`, { text: q }, {
      onDelta: (t) => setDraft((d) => d + t),
      onDone: (d) => { setMessages((m) => [...m, { message_id: d.message_id, role: "assistant", text: d.text }]); setDraft(""); setStreaming(false); },
      onError: (msg) => { toast.error(msg); setDraft(""); setStreaming(false); },
    });
  };
  const clear = async () => { await api.delete(`/projects/${project.project_id}/assistant/messages`); setMessages([]); };

  return (
    <Card className="border-slate-200 flex flex-col h-[560px]" data-testid="project-assistant-panel">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
        <Sparkles className="h-4 w-4 text-slate-900" />
        <div className="flex-1"><div className="text-sm font-semibold text-slate-900">Project Assistant</div><div className="text-[11px] text-slate-500">Answers from this project's phases, evidence, notes and Economic File</div></div>
        <Badge variant="outline" className="font-mono text-[10px] uppercase">{model || "AI"}</Badge>
        <Button size="sm" variant="ghost" onClick={clear} disabled={!messages.length || streaming} title="Clear conversation" data-testid="assistant-clear-btn"><Trash2 className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="assistant-close-btn"><X className="h-4 w-4" /></Button>
      </div>
      <div className="flex-1 overflow-auto px-4 py-3 space-y-3" data-testid="assistant-messages">
        {messages.length === 0 && !draft && (
          <div className="text-sm text-slate-500">
            <p>Ask anything about <strong>{project.project_number}</strong>. Try:</p>
            <div className="mt-2 flex flex-wrap gap-2">{SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-slate-900 hover:text-slate-900 transition" data-testid="assistant-suggestion">{s}</button>)}</div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.message_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white rounded-br-sm" : "bg-slate-100 text-slate-900 rounded-bl-sm"}`}>{m.text}</div>
          </div>
        ))}
        {draft && <div className="flex justify-start" data-testid="assistant-msg-streaming"><div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-100 px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed text-slate-900">{draft}<span className="inline-block w-1.5 h-4 bg-slate-900 ml-0.5 animate-pulse align-middle" /></div></div>}
        {streaming && !draft && <div className="text-xs text-slate-400 animate-pulse">Thinking…</div>}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-100 flex gap-2">
        <Textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask about phases, blockers, evidence, ERP readiness…" className="min-h-[40px] resize-none" data-testid="assistant-input" />
        <Button onClick={() => send()} disabled={streaming || !input.trim()} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="assistant-send-btn"><Send className="h-4 w-4" /></Button>
      </div>
    </Card>
  );
};
